import { getUserById, getWorkspaceById } from "../client/api";
import { MAX_VIDEOS_PER_FREE_USER, Script, User, Workspace } from "../context/types";

//ENUMS
export enum USER_ROLE {
  admin = 'admin',
  user = 'user',
  limited = 'limited',
}

export enum WORKSPACE_CATEGORY {
  fullAccess = 'fullaccess',
  limited = 'limited',
}

export enum USER_WORKSPACE_ROLE {
  admin = 'admin',
  member = 'member',
}

export enum USER_NOTIFICATIONS {
  googleDriveAccess = 'GOOGLE_DRIVE_ACCESS'
}

//FUNCTIONS
export const encodeInvitationKey = (key: string) => {
  return btoa(key); // Use btoa for Base64 encoding in the browser
};

export const fetchUser = async (id?: string) => {
      const userId = id ?? localStorage.getItem('userId'); // Get userId from local storage
      if (userId) {
        try {
          const userData = await getUserById(userId); // Fetch user from API
          return userData
        } catch (error) {
          console.error('Failed to fetch user:', error);
        }
      }
      return null
};

export const fetchWorkspace = async (workspaceId?: string) => {
        if(!workspaceId) return
        const workspace = await getWorkspaceById(workspaceId);
        return workspace;
}

export const canGenerateVideos = (workspace: Workspace | null, user: User | null): boolean => {
  if(!workspace || !user) return false
  const isAppAdmin = user.role === 'admin';
  const workspaceGeneratedVideos = workspace.generatedTestVideos ? workspace.generatedVideos - workspace.generatedTestVideos : workspace.generatedVideos
  const workspaceVideoLimit = !!workspace.plan  ? workspace.plan.videoLimit : MAX_VIDEOS_PER_FREE_USER;
  let hasNotExceededVideoLimit = true;
  if(!!workspace.plan){
    hasNotExceededVideoLimit = workspaceGeneratedVideos < workspaceVideoLimit
  } else {
    hasNotExceededVideoLimit = workspace?.generatedVideos < workspaceVideoLimit
  }

  const isWorkspaceAdmin = workspace.admins?.some(admin => admin._id === user._id);
  const isWorkspaceOwner = workspace.owner._id === user._id;
  const isFullAccessWorkspace = workspace.category === 'fullaccess';
  /*const hasExceededVideoLimit = !workspace.plan && user.videoGenerations.some(
    video => video.workspaceId === workspace._id && video.numberOfGeneratedVideos > 3
  );*/
  const isAdminAndFullAccess = (isWorkspaceOwner || isWorkspaceAdmin) && isFullAccessWorkspace;
  

  // Return true if the user can generate videos
  return (
    isAppAdmin || 
    isAdminAndFullAccess || 
    isFullAccessWorkspace ||
    hasNotExceededVideoLimit 
  );
};

export const canUseFeatures = (workspace: Workspace | null) => { 
  if(!workspace) return false 
  return !!workspace.plan || workspace.category === 'fullaccess'
}

export const isFullAccessWorkspace = (workspace: Workspace | null) => {
  if(!workspace){
    return false
  }
  return workspace.category === 'fullaccess'
}

export const isFreeUser = (workspace: Workspace | null) => {
  if(!workspace){
    return true
  }
  return (workspace.category !== 'fullaccess' && !workspace.plan)
}

export const generateVariableScripts = (script: Script): Script[] => {
  // Make sure we have variableGroups
  const { variableGroups } = ensureVariableGroups(script);

  // If no variables to replace, just return the script as is
  if (variableGroups.length === 0 || variableGroups[0].variables.length === 0) {
    return [script];
  }

  const primaryGroup = variableGroups[0];
  const primaryVariable = primaryGroup.variables[0];
  const scripts: Script[] = [];

  // Helper to replace variables inside headline, body, and cta
  const replaceVars = (base: Script, replacements: Record<string, string>): Script => {
    let headline = base.headline;
    let body = base.body;
    let cta = base.cta;

    Object.entries(replacements).forEach(([varName, value]) => {
      const regex = new RegExp(`\\{\\{${varName}\\}\\}`, "g");
      headline = headline.replace(regex, value);
      body = body.replace(regex, value);
      cta = cta.replace(regex, value);
    });

    return {
      ...base,
      headline,
      body,
      cta
    };
  };

  // Single variable
  if (primaryGroup.variables.length === 1) {
    primaryVariable.values.forEach(valueObj => {
      scripts.push(
        replaceVars(script, { [primaryVariable.name]: valueObj.value })
      );
    });
    return scripts;
  }

  // Multiple variables with correlations
  const secondaryVariable = primaryGroup.variables[1];

  primaryVariable.values.forEach(primaryValue => {
    const correlatedValue = primaryValue.correlations?.[secondaryVariable.name];

    if (correlatedValue) {
      const secondaryValueObj = secondaryVariable.values.find(
        v => v.value === correlatedValue
      );

      if (secondaryValueObj) {
        scripts.push(
          replaceVars(script, {
            [primaryVariable.name]: primaryValue.value,
            [secondaryVariable.name]: secondaryValueObj.value
          })
        );
      }
    } else {
      // If no correlation, just replace primary variable
      scripts.push(
        replaceVars(script, { [primaryVariable.name]: primaryValue.value })
      );
    }
  });

  return scripts;
};

// Generate combinations of variable values
export const generateVariableCombinations = (variables: any[]): Record<string, string>[] => {
    if (!variables || variables.length === 0) return [{}];

    const [currentVar, ...rest] = variables;
    const restCombos = generateVariableCombinations(rest);

    return currentVar.values.flatMap((value: string) =>
        restCombos.map((combo) => ({
            ...combo,
            [currentVar.name]: value,
        }))
    );
};

export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// utils/variables.ts
export const getActiveVariables = (script: Script) => {
  // If using new variable groups system
  if (script.variableGroups?.length) {
    return {
      type: 'groups',
      groups: script.variableGroups
    };
  }
  
  // Fall back to legacy single variable system
  if (script?.variables?.length) {
    return {
      type: 'legacy',
      variables: script.variables
    };
  }
  
  return null;
};

export const canUseLegacyGeneration = (script: Script) => {
  const active = getActiveVariables(script);
  return active?.type === 'legacy' && active?.variables?.length === 1;
};

// utils/variableMigration.ts
export const migrateLegacyVariables = (script: Script): Script => {
  // If already using new format, return as-is
  if (script.variableGroups) return script;
  
  // If no variables at all, ensure we have empty variableGroups
  if (!script.variables || script.variables.length === 0) {
    return {
      ...script,
      variableGroups: [{ name: "Default Group", variables: [] }],
      variables: undefined
    };
  }

  // Migrate from old to new format
  return {
    ...script,
    variableGroups: [{
      name: "Default Group",
      variables: script.variables.map(v => ({
        name: v.name,
        values: v.values.map(val => ({
          value: val,
          correlations: {}
        }))
      }))
    }],
    variables: undefined
  };
};

export const ensureVariableGroups = (script: Script): Required<Pick<Script, 'variableGroups'>> => {
  // If script has variableGroups, return them
  if (script.variableGroups) return { variableGroups: script.variableGroups };
  
  // If script has legacy variables, migrate them
  if (script.variables) {
    return {
      variableGroups: [{
        name: "Default Group",
        variables: script.variables.map(v => ({
          name: v.name,
          values: v.values.map(val => ({
            value: val,
            correlations: {}
          }))
        }))
      }]
    };
  }

  // Default empty structure
  return {
    variableGroups: [{ name: "Default Group", variables: [] }]
  };
};

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);
};

export const formatDate = () => {
  const now = new Date();
  const timeString = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(' ', ''); // Remove space between time and AM/PM
  
  const dateString = now.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
  
  return `${timeString}-${dateString}`;
};